import { defineStore, type StoreGeneric, storeToRefs } from 'pinia';
import { computed, ref, watch, Ref } from 'vue';
import { getViewCollections } from './api';
import { ViewCollectionsQuery } from './types';
import useContentCollectionsStore from './content-collections';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useMyProfiles } from '@ilteducation/profile';
import { Identity } from '@ilteducation/content-collections-types';

type ViewState = {
  isLoading: boolean;
  viewCollectionQuery: ViewCollectionsQuery;
  collections: string[];
};

type State = {
  [viewCollectionId: string]: ViewState | undefined;
};

const useViewCollectionsStore = defineStore('view-collections', () => {
  const viewStates = ref<State>({});

  const isLoadingByView = computed(
    () => (viewCollectionId: Ref<string>) => viewStates.value[viewCollectionId.value]?.isLoading ?? false,
  );
  const viewHasMore = computed(
    () => (viewCollectionId: Ref<string>) =>
      viewStates.value[viewCollectionId.value]?.viewCollectionQuery.cursor !== null,
  );
  const cursor = computed(
    () => (viewCollectionId: Ref<string>) => viewStates.value[viewCollectionId.value]?.viewCollectionQuery.cursor,
  );
  const collectionsByView = computed(() => (viewId: Ref<string>) => {
    const { collectionById } = useContentCollectionsStore();
    return viewStates.value[viewId.value]?.collections.map((collectionId) => collectionById(ref(collectionId))!) ?? [];
  });

  // This is a known type-checking bug that was present in older versions of Pinia, specifically when using storeToRefs() with a mix of ref,
  // computed, and methods in the setup store syntax. Newer versions of Pinia have fixed these type inference issues,
  // allowing the storeToRefs() helper to correctly preserve the types of your properties.
  const { user } = storeToRefs(useUserContext() as unknown as StoreGeneric);
  const { current: currentProfile } = useMyProfiles();
  const identity = computed<Identity>(() => ({
    userId: user.value.uid,
    profileId: currentProfile.value?.id,
  }));

  const $reset = () => {
    viewStates.value = {};
  };

  watch(identity, () => {
    // reset on identity change
    $reset();
  });

  const load = async (viewId: string, viewCollectionQuery: ViewCollectionsQuery): Promise<void> => {
    if (!viewStates.value[viewId]) {
      viewStates.value[viewId] = {
        isLoading: false,
        viewCollectionQuery: { cursor: undefined },
        collections: [],
      };
    }

    const viewState = viewStates.value[viewId]!;

    if (viewState.isLoading || viewState.viewCollectionQuery.cursor === null) {
      return;
    }

    viewState.isLoading = true;
    viewState.viewCollectionQuery = viewCollectionQuery;

    try {
      const { cursor: newCursor, data } = await getViewCollections(
        viewId,
        viewCollectionQuery,
        identity.value.profileId,
      );

      const { put } = useContentCollectionsStore();
      put(data);

      const collectionIds = data.map((collection) => collection.id);
      const currentViewState = viewStates.value[viewId]!;

      /*
       * Dedupe within the page as well as against the pages already held. A view can hand back the
       * same collection twice in one response - a predefined shelf whose selector sits in two of
       * the view's sections arrives once per section - and a set built only from what we already
       * had lets both copies through, which renders the shelf twice.
       */
      const seenIds = new Set(currentViewState.collections);
      const newCollectionIds = collectionIds.filter((id) => {
        if (seenIds.has(id)) {
          return false;
        }
        seenIds.add(id);
        return true;
      });
      currentViewState.collections.push(...newCollectionIds);
      currentViewState.viewCollectionQuery.cursor = newCursor;
    } catch (error) {
      console.error(`Failed to load collections for view ${viewId}:`, error);
    } finally {
      if (viewStates.value[viewId]) {
        viewStates.value[viewId]!.isLoading = false;
      }
    }
  };

  const next = async (viewCollectionId: string): Promise<void> => {
    const viewState = viewStates.value[viewCollectionId];
    if (!viewState) {
      console.error(`Can't paginate view ${viewCollectionId} when it hasn't been loaded.`);
      return;
    }

    await load(viewCollectionId, viewState.viewCollectionQuery);
  };

  const resetState = (viewId: string): void => {
    const viewState = viewStates.value[viewId];
    if (viewState) {
      viewState.isLoading = false;
      viewState.collections = [];
      viewState.viewCollectionQuery.cursor = undefined;
    }
  };

  return {
    cursor,
    collectionsByView,
    isLoadingByView,
    viewHasMore,
    resetState,
    load,
    next,
    $reset,
  };
});

export default useViewCollectionsStore;
